// Auth0 hands back its cached token as long as it is valid for at least this
// long, and only fetches a new one below it.
const AUTH0_MIN_TOKEN_TTL_MS = 60 * 1000
// Refetch a little after the token drops below that, so Auth0 renews it.
const RENEWAL_MARGIN_MS = 5 * 1000
const MIN_REFRESH_DELAY_MS = 5 * 1000
// For a token whose expiry cannot be read.
const FALLBACK_REFRESH_DELAY_MS = 60 * 1000

/** When a JWT expires (its `exp` claim, in ms), or null if it cannot be read. */
export const tokenExpiresAt = (token: string): number | null => {
  const payload = token.split('.')[1]
  if (!payload) return null
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = (JSON.parse(json) as { exp?: unknown }).exp
    return typeof exp === 'number' ? exp * 1000 : null
  } catch {
    return null
  }
}

/**
 * How long until the chat's token is refetched: right after Auth0 stops
 * handing back the cached one, so the header always carries a token valid for
 * close to a minute more, and the refetch gets a fresh one.
 */
export const tokenRefreshDelay = (token: string | undefined, now: number): number => {
  const expiresAt = token ? tokenExpiresAt(token) : null
  if (expiresAt === null) return FALLBACK_REFRESH_DELAY_MS
  return Math.max(
    MIN_REFRESH_DELAY_MS,
    expiresAt - AUTH0_MIN_TOKEN_TTL_MS + RENEWAL_MARGIN_MS - now,
  )
}

// What the API (`/agent-runs/authorize`) and the Copilot runtime answer a
// refused turn with, as the `detail` of the HTTP error.
const DETAIL_MESSAGES: Record<string, string> = {
  agent_chat_not_configured: 'KI-Chat ist in dieser Umgebung nicht eingerichtet',
  agent_quota_exceeded: 'Das monatliche KI-Kontingent Ihrer Organisation ist aufgebraucht',
  agent_project_not_accessible: 'Kein Zugriff auf dieses Projekt',
  agent_thread_not_owned: 'Dieser Chat gehört einem anderen Nutzer',
  agent_thread_busy: 'Der Assistent arbeitet in diesem Chat noch',
  agent_model_unknown: 'Das gewählte Modell ist nicht verfügbar',
  agent_nothing_to_resume: 'Es gibt keine offene Entscheidung, die fortgesetzt werden kann',
  missing_bearer_token: 'Anmeldung fehlt, bitte die Seite neu laden',
}

const CODE_MESSAGES: Record<string, string> = {
  agent_thread_locked: 'Der Assistent arbeitet in diesem Chat noch',
  agent_connect_failed: 'Verbindung zum KI-Chat fehlgeschlagen',
  runtime_info_fetch_failed: 'KI-Chat ist nicht erreichbar',
}

// AG-UI's HttpAgent reports a refused request as `HTTP <status>: <body>`.
const HTTP_ERROR = /^HTTP (\d{3}): (.*)$/s

const httpDetailOf = (message: string): { status: number; detail?: string } | null => {
  const match = HTTP_ERROR.exec(message)
  if (!match) return null
  const status = Number(match[1])
  try {
    const detail = (JSON.parse(match[2]) as { detail?: unknown }).detail
    return { status, detail: typeof detail === 'string' ? detail : undefined }
  } catch {
    return { status }
  }
}

/** The German message for a failed chat request (CopilotKit's `onError`), before its details. */
export const chatErrorMessage = (error: Error, code: string): string => {
  const http = httpDetailOf(error.message)
  const detailMessage = http?.detail ? DETAIL_MESSAGES[http.detail] : undefined
  if (detailMessage) return `KI-Chat: ${detailMessage}`
  if (http?.status === 401) return 'KI-Chat: Anmeldung abgelaufen, bitte die Seite neu laden'
  return `KI-Chat: ${CODE_MESSAGES[code] ?? 'Anfrage an den Assistenten fehlgeschlagen'}`
}
